<template>
  <div>
    <notes
      :notes="mixNotes"
      :owns-mix="true"
      :mix-id="mix.id"
      :current-time="currentTime"
      :highlightedNote="textHighlight"
      @play-at-time="$emit('play-at-time', $event)"
      @note-mouseenter="highlightNote($event, false)"
      @note-mouseleave="dehighlightNote"
    />
    <other-version-notes
      :mix-id="mix.id"
      :notes="otherVersionNotes"
      :owns-mix="mix.policy.destroy"
      :highlightedNote="textHighlight"
      @play-at-time="$emit('play-at-time', $event)"
      @note-mouseenter="highlightNote($event, false)"
      @note-mouseleave="dehighlightNote"
    />
    <previous-notes
      :notes="previousCompletedNotes"
      v-if="previousCompletedNotes.length > 0"
      @play-at-time="$emit('play-at-time', $event)"
    />
    <div style="display: none">
      <note-marker
        v-for="note in coloredNotes"
        :key="note.id"
        ref="noteMarker"
        :data-id="note.id"
        :number="note.number"
        :text="note.note"
        :color="markerHighlight == note.id ? note.highlightColor : note.baseColor"
        @mouseenter="highlightNote(note.id, true)"
        @mouseleave="dehighlightNote(note.id)"
      />
    </div>
  </div>
</template>

<script>
let noteColorIndex = 0

const NOTE_PALLETE = [
  ["#E4572E", "#F07C5A"], // burnt orange → bright coral
  ["#C0392B", "#E05A4F"], // deep red → warm red
  ["#8E44AD", "#B06CD6"], // purple → vibrant violet
  ["#5B2C83", "#8E44AD"], // dark violet → royal purple
  ["#2C3E50", "#4A6278"], // blue-gray → steel blue
  ["#1F618D", "#3498DB"], // deep blue → electric blue
  ["#117864", "#1ABC9C"], // teal → bright teal
  ["#1D8348", "#2ECC71"], // forest green → fresh green
  ["#7D6608", "#C9A227"], // olive → golden mustard
  ["#935116", "#D68910"], // amber → bright amber
  ["#4D5656", "#7F8C8D"], // dark gray → mid gray
  ["#17202A", "#34495E"], // navy → blue slate
]

import Notes from "./notes.vue"
import noteStore from "./note_store.js"
import NoteMarker from "./note_marker.vue"
import OtherVersionNotes from "./other_version_notes.vue"
import PreviousNotes from "./previous_notes.vue"

import { parseNoteTime } from "../utils/note_time"
import { noteTimeToNumeric } from "../utils/note_time"

export default {
  components: { Notes, NoteMarker, OtherVersionNotes, PreviousNotes },
  props: ["mix", "currentTime", "isOpen", "regions"],
  data() {
    return {
      textHighlight: null,
      markerHighlight: null,
    }
  },
  methods: {
    highlightNote(id, doText) {
      this.markerHighlight = id
      if (doText) {
        this.textHighlight = id
        const marker = this.markerNotes.find(n => n.id == id)
        if (marker) {
          const [text, _] = parseNoteTime(marker.note)
          this.$emit("update-note-highlight-text", `${marker.username}: ${text} (#${marker.number})`)
        }
      }
    },
    dehighlightNote(id) {
      this.markerHighlight = null
      this.textHighlight = null
      this.$emit("update-note-highlight-text", "")
    },
    assignNoteColors(notes) {
      notes.forEach(n => {
        if (!n.baseColor && !n.note_parent_id) {
          const [_, time] = parseNoteTime(n.note)
          if (time) {
            const colors = NOTE_PALLETE[noteColorIndex++ % NOTE_PALLETE.length]
            n.baseColor = colors[0]
            n.highlightColor = colors[1]
          }
        }
        return n
      })
    },
    createRegionNotes() {
      if (!this.regions) return
      if (!this.$refs.noteMarker) {
        return
      }

      for (const region of this.regions.regions) {
        if (region.isRemoved) continue

        const note = this.markerNotes.find(n => n.id == region.noteID)
        if (!note) {
          region.remove()
          continue
        }
        const [_, time] = parseNoteTime(note.note)
        if (!time) {
          region.remove()
          continue
        }

        const numericTime = noteTimeToNumeric(time)
        if (region.start != numericTime) region.remove()
      }

      for (const note of this.markerNotes) {
        if (this.regions.regions.find(r => r.noteID == note.id && !r.isRemoved)) continue

        const [text, time] = parseNoteTime(note.note)
        if (!time) continue

        const numericTime = noteTimeToNumeric(time)
        const noteMarker = this.$refs.noteMarker.find(m => m.dataId == note.id)

        noteMarker.textContent = time
        let region = this.regions.addRegion({
          id: note.id,
          start: numericTime,
          drag: false,
          color: note.baseColor,
          content: noteMarker.$el,
        })

        region.noteID = note.id
      }
    },
    updateIncompleteNoteCount() {
      if (this.mixNotes.length > 0)
        this.$emit("update-incomplete-note-count", this.mixNotes.filter(n => !n.complete && !n.parent_note_id).length)
    },
    async fetchNotes() {
      await noteStore.fetchNotes(this.mix.song_id)
    },
  },
  computed: {
    incompleteNoteCount() {
      if (this.mixNotes.length == 0) {
        return this.mix.incomplete_note_count
      } else {
        return this.mixNotes.filter(n => !n.complete && !n.parent_note_id).length
      }
    },
    policy() {
      return this.mix.policy
    },
    previousCompletedNotes() {
      return this.otherVersionNotes.filter(n => n.mix_version == this.mix.version - 1 && n.completed && !n.isLike && !n.parent_note_id)
    },
    mixNotes() {
      const notes = noteStore.songNotes[this.mix.song_id]
      if (!notes) return []

      return notes.filter(n => n.mix_id == this.mix.id)
    },
    otherVersionNotes() {
      const notes = noteStore.songNotes[this.mix.song_id]
      if (!notes) return []

      return notes
        .filter(n => n.mix_id != this.mix.id)
        .filter(n => parseInt(n.mix_version) < parseInt(this.mix.version))
    },
    coloredNotes() {
      this.assignNoteColors(this.mixNotes)
      this.assignNoteColors(this.otherVersionNotes)
      return this.mixNotes.concat(this.otherVersionNotes).filter(n => n.baseColor)
    },
    markerNotes() {
      return this.coloredNotes.filter(n => !(n.completed || n.deleted) && n.note)
    },
  },
  watch: {
    markerNotes() {
      this.$nextTick(() => this.createRegionNotes())
    },
    regions() {
      this.$nextTick(() => this.createRegionNotes())
    },
    incompleteNoteCount(val) {
      this.$emit("update-incomplete-note-count", val)
    },
    isOpen(val) {
      if (val) {
        this.fetchNotes()
      }
    },
  },
  async mounted() {
    if (this.isOpen) {
      await this.fetchNotes()
    }
  },
}
</script>
